import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  CalendarClock,
  ExternalLink,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import SupportTimelineDiagram from '@/components/diagrams/SupportTimelineDiagram';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

// Bump BOTH values whenever a date in this table is re-verified or changed.
const LAST_UPDATED_HUMAN = 'July 7, 2026';
const LAST_UPDATED_ISO = '2026-07-07';

const ORACLE_SOURCES = [
  {
    label: 'Oracle Lifetime Support Policy — Oracle Fusion Middleware (PDF)',
    url: 'https://www.oracle.com/us/assets/lifetime-support-middleware-069163.pdf',
    detail: 'The primary source. Product-by-product Premier, Extended, and Sustaining Support dates, maintained by Oracle.',
  },
  {
    label: 'Oracle Fusion Middleware — End of Support information (docs.oracle.com)',
    url: 'https://docs.oracle.com/en/middleware/fusion-middleware/endsupport.html',
    detail: "Oracle's documentation hub entry for Fusion Middleware end-of-support timelines and upgrade guidance.",
  },
];

interface SupportRow {
  component: string;
  products: string;
  premierEnds: string;
  extendedEnds: string;
  path14c: string;
  window14c: string;
  href: string;
  hrefLabel: string;
  noPath?: boolean;
}

const SUPPORT_ROWS: SupportRow[] = [
  {
    component: 'Oracle WebCenter',
    products: 'Content (UCM), Imaging, Enterprise Capture, Forms Recognition, Portal, Sites',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2)',
    window14c: 'Premier to Dec 2030 · Extended to Dec 2033',
    href: '/solutions/webcenter-14c-upgrade',
    hrefLabel: 'WebCenter 14c upgrade',
  },
  {
    component: 'Oracle SOA Suite',
    products: 'SOA composites, adapters, B2B',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2)',
    window14c: 'Premier to Dec 2030 · Extended to Dec 2033',
    href: '/solutions/oracle-soa-suite-migration',
    hrefLabel: 'SOA Suite migration',
  },
  {
    component: 'Oracle WebLogic Server',
    products: 'The application-server foundation the FMW stack runs on',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2)',
    window14c: 'Premier to Dec 2030 · Extended to Dec 2033',
    href: '/solutions/oracle-weblogic-14c-upgrade',
    hrefLabel: 'WebLogic 14c upgrade',
  },
  {
    component: 'Oracle BPM Suite',
    products: 'Business-process workflows (alongside SOA Suite)',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2)',
    window14c: 'Premier to Dec 2030 · Extended to Dec 2033',
    href: '/solutions/oracle-bpm-suite-migration',
    hrefLabel: 'BPM Suite migration',
  },
  {
    component: 'Oracle ADF',
    products: 'Application Development Framework customizations and forms',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2)',
    window14c: 'Premier to Dec 2030 · Extended to Dec 2033',
    href: '/solutions/oracle-adf-non-po-coding-form-modernization',
    hrefLabel: 'ADF form modernization',
  },
  {
    component: 'Oracle Forms & Reports',
    products: 'Classic Forms applications',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2), released February 2025',
    window14c: '≈8 years Premier-plus-Extended from release',
    href: '/solutions/oracle-forms-12c-to-14c-upgrade',
    hrefLabel: 'Forms 12c → 14c upgrade',
  },
  {
    component: 'Oracle Identity & Access Management',
    products: 'OAM, OIG, OUD, OID',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2.1), released March 2025',
    window14c: '≈8 years Premier-plus-Extended from release',
    href: '/solutions/oracle-identity-management-12c-to-14c-upgrade',
    hrefLabel: 'IAM 12c → 14c upgrade',
  },
  {
    component: 'Oracle Data Integrator (ODI)',
    products: 'ETL / data-integration repositories and agents',
    premierEnds: 'December 2026',
    extendedEnds: 'December 2027',
    path14c: '14c (14.1.2)',
    window14c: 'Premier to Dec 2030 · Extended to Dec 2033',
    href: '/solutions/oracle-data-integrator-12c-to-14c-upgrade',
    hrefLabel: 'ODI 12c → 14c upgrade',
  },
  {
    component: 'OBIEE / Oracle BI 12c',
    products: 'Business Intelligence Enterprise Edition',
    premierEnds: 'Ended',
    extendedEnds: 'Ended August 2025',
    path14c: 'No 14c successor — replace with Oracle Analytics Server (OAS) or Oracle Analytics Cloud (OAC)',
    window14c: '—',
    href: '/solutions/oracle-fusion-middleware-12c-end-of-support',
    hrefLabel: 'Full 12c component map',
    noPath: true,
  },
  {
    component: 'Oracle Endeca',
    products: 'Search / guided discovery',
    premierEnds: 'End of life',
    extendedEnds: '—',
    path14c: 'No 14c successor — requires a replacement search/discovery platform',
    window14c: '—',
    href: '/solutions/oracle-fusion-middleware-12c-end-of-support',
    hrefLabel: 'Full 12c component map',
    noPath: true,
  },
];

const SUPPORT_PHASES = [
  {
    phase: 'Premier Support',
    detail:
      'Full support: new features where applicable, bug fixes, security updates, and new operating-system and database certifications. For Fusion Middleware 12c (12.2.1.4), Premier Support ends December 2026.',
  },
  {
    phase: 'Extended Support',
    detail:
      'Security updates and Severity 1 fixes, for an additional fee — but no new features and no new certifications. For Fusion Middleware 12c, Extended Support ends December 2027, an unusually short 12-month window.',
  },
  {
    phase: 'Sustaining Support',
    detail:
      'Indefinite access to existing patches, fixes, and documentation — but no new patches, no new security updates, and no new certifications. Runs after Extended Support ends. See our dedicated guide to what Sustaining Support does and does not cover.',
  },
  {
    phase: 'Market Driven Support',
    detail:
      'A yearly-renewable program Oracle has offered for selected releases beyond the Extended Support boundary, priced above Extended Support. Availability and scope are set by Oracle per release — confirm current terms with Oracle for 12.2.1.4.',
  },
];

const FAQS = [
  {
    q: 'When does Oracle Fusion Middleware 12c support end?',
    a: "Oracle Fusion Middleware 12c (12.2.1.4) Premier Support ends December 2026 and Extended Support ends December 2027, per Oracle's Lifetime Support Policy. This applies across the 12c stack — WebCenter, SOA Suite, WebLogic Server, BPM, ADF, Forms & Reports, Identity Management, and Data Integrator. After December 2027 the standard option is Sustaining Support (no new patches or certifications).",
  },
  {
    q: 'When does Oracle WebCenter 12c support end?',
    a: 'Oracle WebCenter 12c — WebCenter Content, Imaging, Enterprise Capture, Forms Recognition, Portal, and Sites — follows the Fusion Middleware 12c dates: Premier Support ends December 2026 and Extended Support ends December 2027. WebCenter 14c (14.1.2) is supported through December 2030 (Premier) and December 2033 (Extended).',
  },
  {
    q: 'What is the difference between Premier, Extended, and Sustaining Support?',
    a: 'Premier Support is full support: fixes, security updates, and new OS/database certifications. Extended Support (fee-based) continues security updates and Severity 1 fixes but adds no new features or certifications. Sustaining Support runs indefinitely after Extended Support but provides no new patches, no new security updates, and no new certifications — you retain access to existing patches and documentation only.',
  },
  {
    q: 'Which Fusion Middleware 12c components have a 14c upgrade path?',
    a: 'WebCenter, SOA Suite, WebLogic Server, BPM Suite, ADF, Oracle Forms & Reports, Identity & Access Management, and Data Integrator all have a supported 12c → 14c (14.1.2) upgrade path. Two components do not: OBIEE / Oracle BI 12c (forward path is Oracle Analytics Server or Oracle Analytics Cloud; its Extended Support already ended August 2025) and Oracle Endeca (end of life; requires a replacement search platform).',
  },
  {
    q: 'From which 12c version can I upgrade to 14c?',
    a: 'Oracle supports upgrades to 14c (14.1.2) only from 12.2.1.4 (or 12.2.1.19). Implementations on older 12c releases must first move to 12.2.1.4 before the 14c upgrade.',
  },
  {
    q: 'How long is Oracle Fusion Middleware 14c supported?',
    a: 'Fusion Middleware 14c (14.1.2) carries roughly eight years of Premier-plus-Extended runway — for WebCenter 14c, Premier Support runs through December 2030 and Extended Support through December 2033. Forms & Reports 14c (released February 2025) and Identity & Access Management 14c (14.1.2.1, released March 2025) carry approximately eight years from their release dates.',
  },
  {
    q: 'Where does Oracle publish the official support dates?',
    a: "In the Oracle Lifetime Support Policy for Oracle Fusion Middleware (a PDF Oracle maintains at oracle.com) and on the Fusion Middleware end-of-support page at docs.oracle.com. Both are linked from this page. This page is a consolidated reference; Oracle's documents are always the authoritative source.",
  },
];

const OracleFmwSupportDates = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle Fusion Middleware & WebCenter Support Dates — Consolidated Reference',
    description:
      'Every Oracle Fusion Middleware 12c component with its Premier and Extended Support end dates, its 14c upgrade path (or replacement path), and the 14c support window — consolidated from Oracle\'s Lifetime Support Policy and kept current.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: LAST_UPDATED_ISO,
    dateModified: LAST_UPDATED_ISO,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/resources/oracle-fusion-middleware-support-dates',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 12c' },
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 14c' },
      { '@type': 'Thing', name: 'Oracle WebCenter' },
      { '@type': 'Thing', name: 'Oracle Lifetime Support Policy' },
      { '@type': 'Thing', name: 'Oracle Premier Support' },
      { '@type': 'Thing', name: 'Oracle Extended Support' },
      { '@type': 'Thing', name: 'Oracle Sustaining Support' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle Fusion Middleware & WebCenter Support Dates (Updated July 2026) | EZ Cloud"
        description="Consolidated reference: every Oracle Fusion Middleware 12c component with Premier/Extended Support end dates (Dec 2026 / Dec 2027), its 14c upgrade path or replacement path, and the 14c support window — with links to Oracle's Lifetime Support Policy."
        canonical="https://ezcloud.ai/resources/oracle-fusion-middleware-support-dates"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/webcenter-decision-guide' },
          {
            name: 'Oracle FMW Support Dates',
            url: 'https://ezcloud.ai/resources/oracle-fusion-middleware-support-dates',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <CalendarClock className="w-4 h-4" aria-hidden />
              Reference · Oracle support timelines
            </div>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Oracle Fusion Middleware &amp; WebCenter Support Dates
              <span className="block text-accent mt-3 text-2xl md:text-3xl font-bold">
                Every 12c component, every deadline, every 14c path — one table.
              </span>
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mb-6">
              Oracle publishes the dates across a policy PDF, documentation pages, and support
              notes. This page consolidates them: the December 2026 / December 2027 boundaries for
              Fusion Middleware 12c, the 14c path (or replacement path) per component, and the 14c
              support window — with links to Oracle's primary sources.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-sm text-white/90">
              <ShieldCheck className="w-4 h-4 text-accent" aria-hidden />
              Last updated {LAST_UPDATED_HUMAN} · verified against Oracle's Lifetime Support Policy
            </div>
          </div>
        </section>

        {/* TL;DR — direct answer for AEO synthesis */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The short answer
            </p>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle Fusion Middleware 12c (12.2.1.4) Premier Support ends December 2026 and
                  Extended Support ends December 2027
                </strong>
                , per Oracle's Lifetime Support Policy. This covers WebCenter (Content, Imaging,
                Enterprise Capture, Forms Recognition, Portal, Sites), SOA Suite, WebLogic Server,
                BPM Suite, ADF, Forms &amp; Reports, Identity &amp; Access Management, and Data
                Integrator.
              </p>
              <p>
                Most components have a supported upgrade to{' '}
                <strong className="text-gray-900">14c (release 14.1.2)</strong> — supported only
                from 12.2.1.4 or 12.2.1.19. Two do not: OBIEE / Oracle BI 12c (whose Extended
                Support already ended August 2025; forward path is Oracle Analytics Server or
                Oracle Analytics Cloud) and Oracle Endeca (end of life).
              </p>
            </div>
          </div>
        </section>

        {/* Timeline visual */}
        <section className="py-14 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The window, drawn
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              The 12c-to-14c support timeline
            </h2>
            <SupportTimelineDiagram />
          </div>
        </section>

        {/* THE TABLE */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The consolidated table
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Support dates by Fusion Middleware 12c component
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
              Dates per Oracle's Lifetime Support Policy for Oracle Fusion Middleware. Each
              component links to our dedicated upgrade or decision page.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-4 py-4">
                      Component
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-4 py-4">
                      12c Premier ends
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-4 py-4">
                      12c Extended ends
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-4 py-4">
                      14c path
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-4 py-4">
                      14c support window
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SUPPORT_ROWS.map((row) => (
                    <tr key={row.component} className="border-b border-gray-200 last:border-b-0 align-top">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{row.component}</div>
                        <div className="text-xs text-gray-500 mt-1">{row.products}</div>
                        <Link
                          to={row.href}
                          className="inline-flex items-center text-xs font-semibold text-primary hover:text-accent transition-colors mt-2"
                        >
                          {row.hrefLabel}
                          <ArrowRight className="w-3 h-3 ml-1" aria-hidden />
                        </Link>
                      </td>
                      <td className="px-4 py-4 text-gray-700 whitespace-nowrap">{row.premierEnds}</td>
                      <td className="px-4 py-4 text-gray-700 whitespace-nowrap">{row.extendedEnds}</td>
                      <td className={`px-4 py-4 ${row.noPath ? 'text-amber-700' : 'text-gray-700'}`}>
                        {row.path14c}
                      </td>
                      <td className="px-4 py-4 text-gray-700">{row.window14c}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-sm text-gray-500 mt-6 max-w-3xl mx-auto">
              Upgrades to 14.1.2 are supported only from 12.2.1.4 (or 12.2.1.19). Older 12c
              releases must first move to 12.2.1.4. For the full component-by-component decision
              map, see the{' '}
              <Link
                to="/solutions/oracle-fusion-middleware-12c-end-of-support"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                Fusion Middleware 12c end-of-support guide
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Support phases explained */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The support phases
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What each boundary actually means
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The dates only matter in the context of what stops at each one. Oracle's Lifetime
              Support Policy defines four phases relevant to the 12c decision.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SUPPORT_PHASES.map((p) => (
                <Card key={p.phase} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-semibold mb-2">{p.phase}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{p.detail}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-8 max-w-3xl mx-auto">
              Weighing whether to stay on Sustaining Support instead of upgrading? See{' '}
              <Link
                to="/solutions/webcenter-sustaining-support-decision"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                the WebCenter Sustaining Support decision guide
              </Link>{' '}
              and our explainer on{' '}
              <Link
                to="/blog/oracle-sustaining-support-vs-extended-support-12c"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                Sustaining vs Extended Support for 12c
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Oracle primary sources */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-2 text-accent text-xs font-bold uppercase tracking-wide mb-4">
              <FileText className="w-4 h-4" aria-hidden />
              Oracle's primary sources
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Verify every date against Oracle's own documents
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              This page is a consolidated reference. Oracle's documents are the authoritative
              source — if Oracle revises a date, their documents govern and this page gets updated.
            </p>
            <div className="space-y-3">
              {ORACLE_SOURCES.map((s) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-accent transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <ExternalLink className="w-4 h-4 text-accent flex-shrink-0 mt-1" aria-hidden />
                    <div>
                      <div className="font-semibold text-gray-900">{s.label}</div>
                      <div className="text-sm text-gray-600 mt-1">{s.detail}</div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-6">
              Deeper reading on the timeline:{' '}
              <Link
                to="/blog/oracle-fusion-middleware-12c-end-of-support-dates"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                FMW 12c end-of-support dates explained
              </Link>{' '}
              ·{' '}
              <Link
                to="/blog/oracle-webcenter-2026-support-timeline"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                the Oracle WebCenter 2026 support timeline
              </Link>
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the support dates
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Turn the dates into a plan
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist, maps which of your components upgrade, which replace, and what
              leads the sequencing into the December 2026 / 2027 window.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('fmw-support-dates-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full 12c component guide
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleFmwSupportDates;
